"use client";

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, ChevronDown } from "lucide-react";
import { ParsedMessageContent } from "@/components/ai-elements/parsed-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export interface TriggerMessageMeta {
  firedAt: string;
  timezone?: string;
  schedule?: string;
  scheduleId?: string;
}

export interface TriggerMessage {
  trigger: string;
  job: string;
  message: string;
  oneShot: boolean;
  plainText?: boolean;
  meta: TriggerMessageMeta;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Recognize the scheduler's persisted user-message envelope without treating
 * ordinary pasted JSON as a trigger. Scheduler messages always include the
 * complete `_meta` block, so require it as part of the UI contract.
 */
export function parseTriggerMessage(text: string): TriggerMessage | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (
      !isRecord(parsed)
      || typeof parsed.trigger !== "string"
      || typeof parsed.message !== "string"
      || !isRecord(parsed._meta)
    ) {
      return null;
    }

    const meta = parsed._meta;
    if (
      typeof meta.fired_at !== "string"
      || typeof meta.timezone !== "string"
      || typeof meta.schedule !== "string"
      || typeof meta.schedule_id !== "string"
    ) {
      return null;
    }

    return {
      trigger: parsed.trigger,
      // `job` is part of current scheduler payloads. Falling back to the
      // trigger keeps older valid envelopes readable without widening the
      // detection contract beyond scheduler-shaped messages.
      job: typeof parsed.job === "string" ? parsed.job : parsed.trigger,
      message: parsed.message,
      oneShot: parsed._one_shot === true,
      meta: {
        firedAt: meta.fired_at,
        timezone: meta.timezone,
        schedule: meta.schedule,
        scheduleId: meta.schedule_id,
      },
    };
  } catch {
    return null;
  }
}

export function formatTriggerMessageTime(
  firedAt: string,
  locale: string,
): string {
  const date = new Date(firedAt);
  if (!Number.isFinite(date.getTime())) return firedAt;

  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return firedAt;
  }
}

interface TriggerDetailRowProps {
  label: string;
  value: string;
}

function TriggerDetailRow({ label, value }: TriggerDetailRowProps) {
  return (
    <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all font-mono text-muted-foreground">{value}</dd>
    </div>
  );
}

export interface TriggerMessageCardProps {
  triggerMessage: TriggerMessage;
  onFileLinkClick?: (url: string, fileName: string) => void;
}

/** A compact, human-readable renderer for persisted scheduled-trigger prompts. */
export function TriggerMessageCard({
  triggerMessage,
  onFileLinkClick,
}: TriggerMessageCardProps) {
  const { t, i18n } = useTranslation("chat");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const generatedId = useId();
  const detailsId = `trigger-message-details-${generatedId}`
    .replace(/[^A-Za-z0-9_-]/g, "");
  const triggerName = triggerMessage.trigger || t("triggerMessage.scheduledRun");
  const formattedFiredAt = formatTriggerMessageTime(
    triggerMessage.meta.firedAt,
    i18n.language,
  );

  return (
    <Card asChild className="w-full p-3 text-left">
      <section
        aria-label={t("triggerMessage.cardLabel", {
          trigger: triggerName,
        })}
        data-testid="trigger-message-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <BellRing
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-foreground"
            />
            <span
              className="truncate text-sm font-medium"
              title={triggerName}
            >
              {triggerName}
            </span>
            {triggerMessage.oneShot && (
              <Badge
                className="shrink-0 px-1.5 py-0 text-[10px] font-medium"
                variant="secondary"
              >
                {t("triggerMessage.oneShot")}
              </Badge>
            )}
          </div>
          {triggerMessage.meta.firedAt && <time
            className="shrink-0 text-[11px] text-muted-foreground"
            dateTime={triggerMessage.meta.firedAt}
            title={triggerMessage.meta.firedAt}
          >
            {formattedFiredAt}
          </time>}
        </div>

        <Separator className="my-2.5" />

        <div className="min-w-0">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {t("triggerMessage.prompt")}
          </p>
          {triggerMessage.plainText ? (
            <div className="whitespace-pre-wrap break-words">{triggerMessage.message}</div>
          ) : (
            <ParsedMessageContent
              text={triggerMessage.message}
              onFileLinkClick={onFileLinkClick}
            />
          )}
        </div>

        <Collapsible
          className="mt-2"
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
        >
          <CollapsibleTrigger asChild>
            <Button
              aria-controls={detailsId}
              aria-expanded={detailsOpen}
              aria-label={t(
                detailsOpen
                  ? "triggerMessage.hideDetails"
                  : "triggerMessage.showDetails",
              )}
              className="h-7 gap-1.5 px-1.5 text-xs text-muted-foreground hover:bg-background/60 hover:text-foreground"
              size="sm"
              type="button"
              variant="ghost"
            >
              <span>{t("triggerMessage.details")}</span>
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "size-3 transition-transform",
                  detailsOpen && "rotate-180",
                )}
              />
            </Button>
          </CollapsibleTrigger>

          {detailsOpen && (
            <CollapsibleContent
              aria-label={t("triggerMessage.details")}
              className="pt-1.5"
              id={detailsId}
              role="region"
            >
              <dl className="space-y-1.5 rounded-md border bg-background/50 p-2.5 text-xs">
                {triggerMessage.job && <TriggerDetailRow
                  label={t("triggerMessage.job")}
                  value={triggerMessage.job}
                />}
                {triggerMessage.meta.schedule && <TriggerDetailRow
                  label={t("triggerMessage.schedule")}
                  value={triggerMessage.meta.schedule}
                />}
                {triggerMessage.meta.timezone && <TriggerDetailRow
                  label={t("triggerMessage.timezone")}
                  value={triggerMessage.meta.timezone}
                />}
                {triggerMessage.meta.scheduleId && <TriggerDetailRow
                  label={t("triggerMessage.scheduleId")}
                  value={triggerMessage.meta.scheduleId}
                />}
                {triggerMessage.meta.firedAt && <TriggerDetailRow
                  label={t("triggerMessage.firedAt")}
                  value={triggerMessage.meta.firedAt}
                />}
              </dl>
            </CollapsibleContent>
          )}
        </Collapsible>
      </section>
    </Card>
  );
}
